// React Dependencies
import React, { useState, useCallback, useRef } from 'react';

// External Dependencies
import _ from 'lodash';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import MaterialTable from 'material-table';
import { Box, FormControl, InputAdornment, TextField } from '@material-ui/core';
import { toast } from 'react-toastify';
import { Search } from '@material-ui/icons';

// Internal Dependencies
import apiService from '../../../services.js/apiService';
import { updateModalContent, toggleModal } from '../../../actions/modal';
import { formatDateToDayMonthNameYear, showDateInSGTime } from '../../../common/util';
import { IsEmpty } from '../../../common/checks';
import { Status } from '../../../common/constants';

const pageOptions = [5, 10, 15, 20, 25];

// eslint-disable-next-line no-shadow, react/prop-types
const VouchersAndCouponsTable = ({ pageId, updateModalContent, toggleModal }) => {
  const history = useHistory();
  const tableRef = useRef(null);

  const [pageSize, setPageSize] = useState(pageOptions[0]);
  const [search, setSearch] = useState(''); // the searchBar component state
  const [searchQuery, setSearchQuery] = useState(''); // the table query search state
  const [searchFocus, setSearchFocus] = useState(false);

  const columns = [
    { title: 'Id', field: 'id', filtering: false, width: '100%' },
    {
      title:  'Image',
      render: (gift) => {
        let giftImage = <p className="m-0 p-0">Image</p>;

        if (!IsEmpty(gift.images)) {
          giftImage = (
            <img
              src={gift.images[0].display}
              style={{ width: '100%', height: '5.625rem', objectFit: 'cover', borderRadius: '4px' }}
              alt={gift.label}
            />
          );
        }

        return (
          <div
            className="d-flex justify-content-center align-items-center py-4"
            style={{ width: '10rem', height: '5.625rem', backgroundColor: '#D9D9D9', textAlign: 'center', borderRadius: '4px' }}
          >
            {giftImage}
          </div>
        );
      },
      width: '100%',
    },
    { title: 'Type', field: 'type', filtering: false, width: '7.5%' },
    { title: 'Description', field: 'label', filtering: false, width: '100%' },
    { title: 'Status', field: 'status', filtering: false, width: '5%' },
    {
      title:  'Start Date',
      field:  'startDate',
      render: (gift) => (gift.startDate !== null ? `${formatDateToDayMonthNameYear(showDateInSGTime(gift.startDate))}` : '-'),
      width:  '100%',
    },
    {
      title:  'Expiry Date',
      field:  'endDate',
      render: (gift) => (gift.endDate !== null ? `${formatDateToDayMonthNameYear(showDateInSGTime(gift.endDate))}` : '-'),
      width:  '100%',
    },
    {
      title:  'Extended Expiry Date',
      field:  'extendedEndDate',
      render: (gift) => (gift.extendedEndDate !== null ? `${formatDateToDayMonthNameYear(showDateInSGTime(gift.extendedEndDate))}` : '-'),
      width:  '100%',
    },
    { title: 'Last Updated By', field: 'lastUpdatedBy', width: '10%' },
  ];

  const debounceSearchQuery = useCallback(
    _.debounce((text) => {
      setSearchQuery(text);
      tableRef.current.onQueryChange();
    }, 500), [],
  );

  const handleSearchChange = (e) => {
    const { value } = e.target;

    setSearch(value);
    setSearchFocus(true);
    debounceSearchQuery(value);
  };

  const handleDelete = (giftId) => {
    updateModalContent({
      title:        'Are you sure?',
      content:      'All data related to this gift will be deleted!',
      actionButton: {
        actionText: 'Yes, Delete',
        actionType: 'danger',
        action:     async () => {
          try {
            const result = await apiService.deleteGift({ pageId, giftId });

            if (result) {
              tableRef.current.onQueryChange();
            }
            toast.success('Gift deleted successfully');
          } catch (err) {
            toast.error(err.message);
          }
        },
      },
      cancelText:       'No',
      closeAfterAction: true,
    });

    toggleModal();
  };

  return (
    <MaterialTable
      tableRef={tableRef}
      title=""
      columns={columns}
      components={{ Toolbar: () => (
        <div className="pt-3">
          <Box display="flex" justifyContent="flex-start" m={0} p={1} bgcolor="background.paper">
            <Box p={1} width="23%">
              <FormControl fullWidth>
                <TextField
                  label="Search Here "
                  id="standard-adornment-amount"
                  placeholder="Search by Description"
                  value={search}
                  onChange={handleSearchChange}
                  type="search"
                  startadornment={(
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  )}
                  autoFocus={searchFocus}
                  alignself="flex-end"
                />
              </FormControl>
            </Box>
          </Box>
        </div>
      ) }}
      onChangeRowsPerPage={(size) => setPageSize(size)}
      data={(query) => new Promise(async (resolve) => {
        try {
          query.pageNo = query.page;
          query.order = { col: 'createdAt', dir: 'desc' }; //default sort is descending gift Start Date
          query.label = searchQuery;

          if (!IsEmpty(query.orderDirection) && !IsEmpty(query.orderBy)) {
            query.order = { col: query.orderBy.field, dir: query.orderDirection };
          }

          const result = await apiService.getGiftsByParams(pageId, { ...query });

          const { rows, totalCount } = result;
          const { page } = query;

          resolve({ data: rows, page, totalCount });
        } catch (err) {
            toast.error(err.message);
            resolve({
              data:       [],
              page:       0,
              totalCount: 0,
            });
        }
      })}
      actions={[
        {
          icon:    'edit',
          tooltip: 'View/Edit Gift',
          onClick: (event, gift) => {
            history.push(`/admin/page/${pageId}/vouchers-coupons/${gift.id}`);
          },
        },
        (rowData) => ({
          hidden:  rowData.status !== Status.Draft,
          icon:    'delete',
          tooltip: 'Delete Gift',
          onClick: () => {
            handleDelete(rowData.id);
          },
        }),
      ]}
      options={{
        actionsColumnIndex:  -1,
        sorting:             true,
        paging:              true,
        emptyRowsWhenPaging: false,
        pageSize,
        pageSizeOptions:     pageOptions,
      }}
    />
  );
};

VouchersAndCouponsTable.propTypes = {
  pageId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

const mapDispatchToProps = (dispatch) => ({
  toggleModal:        () => dispatch(toggleModal()),
  updateModalContent: (data) => dispatch(updateModalContent(data)),
});

export default connect(null, mapDispatchToProps)(VouchersAndCouponsTable);
